import { useState, useRef, type ReactNode, type TouchEvent } from 'react';
import { Trash2 } from 'lucide-react';
import { haptics } from '../../lib/haptics';

interface SwipeableCardProps {
  children: ReactNode;
  onDelete: () => void;
  threshold?: number;
  className?: string;
  disabled?: boolean;
}

export function SwipeableCard({
  children,
  onDelete,
  threshold = 80,
  className = '',
  disabled = false,
}: SwipeableCardProps) {
  const [translateX, setTranslateX] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const isHorizontalSwipe = useRef<boolean | null>(null);

  const handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    isHorizontalSwipe.current = null;
  };

  const handleTouchMove = (e: TouchEvent) => {
    // Ignore swipe if disabled
    if (disabled) return;

    const touch = e.touches[0];
    if (!touch) return;

    const currentX = touch.clientX;
    const currentY = touch.clientY;
    const diffX = currentX - startX.current;
    const diffY = currentY - startY.current;

    // Determine swipe direction on first significant movement
    if (isHorizontalSwipe.current === null) {
      if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
        isHorizontalSwipe.current = Math.abs(diffX) > Math.abs(diffY);
      }
    }

    // Only handle horizontal swipes to the left
    if (isHorizontalSwipe.current && diffX < 0) {
      const newTranslateX = Math.max(diffX, -threshold - 40);
      setTranslateX(newTranslateX);

      // Haptic feedback when reaching threshold
      if (Math.abs(newTranslateX) >= threshold && !isDeleting) {
        setIsDeleting(true);
        haptics.warning();
      } else if (Math.abs(newTranslateX) < threshold && isDeleting) {
        setIsDeleting(false);
      }
    }
  };

  const handleTouchEnd = () => {
    if (Math.abs(translateX) >= threshold) {
      haptics.medium();
      onDelete();
    }
    setTranslateX(0);
    setIsDeleting(false);
    isHorizontalSwipe.current = null;
  };

  const deleteOpacity = Math.min(Math.abs(translateX) / threshold, 1);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Delete indicator background */}
      <div
        className="absolute inset-y-0 right-0 flex items-center justify-end px-4 bg-destructive"
        style={{ opacity: deleteOpacity }}
      >
        <Trash2
          className={`h-5 w-5 text-destructive-foreground transition-transform ${
            isDeleting ? 'scale-110' : 'scale-100'
          }`}
        />
      </div>

      {/* Main content */}
      <div
        className="relative bg-background transition-transform duration-75 ease-out"
        style={{
          transform: `translateX(${translateX}px)`,
          transitionDuration: translateX === 0 ? '200ms' : '0ms',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}
